/**
 * TopBar Component - Enhanced with NotificationCenter (APB-1296)
 *
 * Epic: APB-1306 - [Notification] EPIC-2 - Approval Workflow Notifications (P1)
 *
 * Main application top bar with navigation, user info, and notification center.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { NotificationCenter } from '../notifications';

interface TopBarProps {
  tenant: string;
  username: string;
  onMenuClick: () => void;
  showRoleSwitcher: boolean;
}

export const TopBar: React.FC<TopBarProps> = ({
  tenant,
  username,
  onMenuClick,
  showRoleSwitcher
}) => {
  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-gray-900 border-b border-gray-800 z-50">
      <div className="flex items-center justify-between h-full px-4">
        {/* Left Section */}
        <div className="flex items-center space-x-4">
          <button onClick={onMenuClick} className="text-gray-400 hover:text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="text-purple-500 font-semibold">
            {tenant}<span className="text-gray-500">-brain.astrapeak.ai</span>
          </div>
        </div>

        {/* Center Section - Empty */}
        <div className="flex-1"></div>

        {/* Right Section */}
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-400">{username}</div>

          {/* Settings Button */}
          <button className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-800 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>

          {/* Notification Center - APB-1296 */}
          <NotificationCenter />

          {/* Logout Button */}
          <Link
            to="/logout"
            className="px-3 py-1 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white
                       rounded-md text-sm font-medium border border-gray-700"
          >
            Logout
          </Link>

          {/* User Avatar */}
          <div className="h-8 w-8 rounded-full bg-purple-600 flex items-center justify-center">
            {username ? username.charAt(0).toUpperCase() : '?'}
          </div>
        </div>
      </div>
    </header>
  );
};
